import React, { useContext, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./Login.module.scss";
import { AuthContext } from "../../context/Auth/AuthContext";
import { GamestateContext } from "../../context/GameState/GameStateContext";
import CrossIcon from "../../assets/icons/error.svg";
import CheckIcon from "../../assets/icons/check.svg";
import CodeValidation from "../../schemas/Validation/Login";
import { Formik } from "formik";
import TooltipIcon from "../../assets/icons/tooltip.svg";
import  ReactTooltip  from "react-tooltip";
import { CircularSpinner } from "../../components/Spinner/Spinner";
import Button from "../../components/button/button";
import InputField from "../../components/inputfield/inputfield";
import NetworkManager from "../../services/api.services";
import { GameLanguages, EGameScreen, EGameCompany } from "../Enums/Enums";
import { PopupContext } from "../../context/Popup/PopupContext";
import {
  CHANNEL_JOIN_FAILURE,
  CHANNEL_JOIN_SUCCESS,
  MAX_USERS,
} from "../../constants";
import pusher from "../../config/pusher";

const Login: React.FC = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    setGameCode,
    setGameScreen,
    gameCode,
    gameLanguage,
    setGameType,
    setGameCompany,
    setGameLanguage,
  } = useContext(GamestateContext);
  const { showLoading, isLoading, popup } = useContext(PopupContext);

  const [serverStatus, checkServerStatus] = React.useState<string>("");
  const [hasError, setError] = React.useState<boolean>(false);
  // const [isLoading, setLoading] = useState<boolean>(false);
  // const logger = useWinstonLogger();
  useEffect(() => {
    if (authContext.checkAuthentication()) {
      navigate("/")
      return;
    }
    pusher.unsubscribe(`presence-${gameCode}`);
  }, []);
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setGameScreen(EGameScreen.Login);
      setGameLanguage(GameLanguages.ENUK);
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const onTestConnectionButtonClick = async () => {
    try {
      let memberChannel = pusher.subscribe(`presence-${"veautomation"}`);
      memberChannel.bind(CHANNEL_JOIN_SUCCESS, async () => {
        try {
          await NetworkManager.HealthCheck();
          await NetworkManager.GetMemberCount("veautomation");
          setError(false);
          checkServerStatus("Connection successful");
        } catch (err) {
          setError(true);
          checkServerStatus("Connection failed");
        } finally {
          memberChannel.unbind_all();
          memberChannel.unsubscribe();
        }
      });
      memberChannel.bind(CHANNEL_JOIN_FAILURE, (err: any) => {
        setError(true);
        checkServerStatus("Connection failed");
        memberChannel.unbind_all();
        memberChannel.unsubscribe();
      });
    } catch (err) {
      setError(true);
      checkServerStatus("Connection failed");
    }
  };
  const handleClick = async ({ code }: any) => {
    code = code.toUpperCase();
    showLoading(true);
    let response = null;
    try {
      response = await NetworkManager.HealthCheck();
    } catch (err) {
      showLoading(false);
      setError(true);
      checkServerStatus("Could not reach server");
      return;
    }

    try {
      response = await NetworkManager.GetMemberCount(code);
      if (response.data.totalMembers >= MAX_USERS) {
        showLoading(false);
        setError(true);
        checkServerStatus(
          "The maximum number of players is already logged in."
        );
        // let box = getMessageBox(
        //   false,
        //   "Room is Full",
        //   [
        //     "The maximum number of players is already logged in. Please try logging in with a different game code.",
        //   ],
        //   "TRY AGAIN",
        //   (value) => {}
        // );
        // setMessageBox(box);
        // setMessageBoxStatus(true);
        return;
      }
    } catch (err) {
      showLoading(false);
      setError(true);
      checkServerStatus("Could not reach server");
      return;
    }

    try {
      response = await NetworkManager.Login(code, gameLanguage);
    } catch (err) {
      showLoading(false);
      setError(true);
      checkServerStatus("Invalid game code provided");
      return;
    }
    if (response.status === 200) {
      showLoading(false);
      authContext.authenticateUser(response.data.token);
      setGameCode(code.toUpperCase());
      setGameType(response.data.gameType);
      setGameCompany(
        response.data.reseller ? response.data.reseller : EGameCompany.Experios
      );
      navigate("/");
    }
  };

  const loginPage = () => {
    return (
      <Fragment>
        {isLoading ? <CircularSpinner></CircularSpinner> : null}
        {popup ? popup : null}
        <div className={classes["App"]}>
          <header className={classes["App-header"]}>
            <div className={classes["container"]}>
              <span className={classes["title"]}>VIRTUAL ESCAPE</span>
              <span className={classes["instruction"]}>
                Enter your game code to get started
              </span>
              <Formik
                initialValues={{
                  code: "",
                }}
                validationSchema={CodeValidation}
                onSubmit={handleClick}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => {
                  return (
                    <form
                      onSubmit={handleSubmit}
                      className={classes["form-container"]}
                      autoComplete="off"
                    >
                      <div className={classes["input-container"]}>
                        <InputField
                          autoFocus
                          value={values.code?.toUpperCase()}
                          onValueChanged={handleChange}
                          placeholder="Game Code"
                          id="code"
                          name="code"
                          maxLength={8}
                          style={{ width: "60%" }}
                        ></InputField>
                        <img
                          className={classes["tooltip"]}
                          src={TooltipIcon}
                          data-tip
                          data-for="inputTooltip"
                          alt=""
                        ></img>
                        <ReactTooltip
                          id="inputTooltip"
                          type="error"
                          effect="solid"
                          backgroundColor="#B91213"
                          multiline
                        >
                          <span>
                            Your game code is 8 characters long and can be{" "}
                            <br></br> found in the confirmation email. Up to 6
                            players<br></br>can use the same game code to log
                            in.
                          </span>
                        </ReactTooltip>
                      </div>
                      <div className={classes["button-container"]}>
                        <Button
                          className="mt-3"
                          text="TEST CONNECTION"
                          color="grey"
                          type="button"
                          style={{
                            width: "49%",
                            height: "58px",
                            fontSize: "1em",
                          }}
                          onClick={() => {
                            onTestConnectionButtonClick();
                          }}
                        ></Button>
                        <Button
                          className="mt-3"
                          text="LOG IN"
                          type="submit"
                          icon={<i className="fas fa-fingerprint"></i>}
                          disabled={
                            isSubmitting ||
                            values.code.length !== 8 ||
                            isLoading
                          }
                          style={{
                            width: "49%",
                            height: "58px",
                            fontSize: "1em",
                          }}
                        ></Button>
                      </div>

                      {!serverStatus ? (
                        <span></span>
                      ) : (
                        <div
                          className={
                            classes[hasError ? "error-text" : "success-text"]
                          }
                        >
                          <img
                            src={hasError ? CrossIcon : CheckIcon}
                            alt="icon"
                          ></img>{" "}
                          &nbsp;
                          <span>{serverStatus}</span>
                        </div>
                      )}
                    </form>
                  );
                }}
              </Formik>
            </div>
            <span className={classes["version-no"]}>
              {/* ©Release v3.0.3 */}v7.2.0
            </span>
          </header>
        </div>
      </Fragment>
    );
  };

  return <Fragment>{loginPage()}</Fragment>;
};

export default Login;
